import { LoadingOutlined } from "@ant-design/icons";
import { Button, Carousel, Col, Row, Image, Modal } from "antd";
import imageCompression from "browser-image-compression";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { SignedInContext } from "../AppEnv";
import { SignedInDoctor } from "../auth/interfaces";
import { emptyPatient, Patient } from "../patients/interfaces";
import { getPatient } from "../patients/utils";
import ExamImage from "./ExamImage";

import {
  EXAM_4PHOTOS_FULLNAME,
  EXAM_4PHOTOS_SLUGNAME,
  EXAM_AAII_FULLNAME,
  EXAM_AAII_SLUGNAME,
  EXAM_BALANCE_FULLNAME,
  EXAM_BALANCE_SLUGNAME,
  EXAM_MAGNO_FULLNAME,
  EXAM_MAGNO_SLUGNAME,
  EXAM_PODO_FULLNAME,
  EXAM_PODO_SLUGNAME,
  EXAM_PODO_STIM_FULLNAME,
  EXAM_PODO_STIM_SLUGNAME,
  EXAM_VISION_FULLNAME,
  EXAM_VISION_SLUGNAME,
  EXAM_HEAD_FULLNAME,
  EXAM_HEAD_SLUGNAME,
  EXAM_FUKUDA_FULLNAME,
  EXAM_FUKUDA_SLUGNAME,
  EXAM_ADAMS_FULLNAME,
  EXAM_ADAMS_SLUGNAME,
  EXAM_ROMBERG_FULLNAME,
  EXAM_ROMBERG_SLUGNAME,
  EXAM_RATE_FULLNAME,
  EXAM_RATE_SLUGNAME,
  EXAM_ANAM_FULLNAME,
  EXAM_ANAM_SLUGNAME,
  EXAM_TINETTI_FULLNAME,
  EXAM_TINETTI_SLUGNAME,
  EXAM_ELICOIDALE_FULLNAME,
  EXAM_ELICOIDALE_SLUGNAME,
} from "./exams_definitions_utils";
import {
  invalidPosturalExamination,
  PosturalExamination,
  PosturalTestDefinition,
} from "./interfaces";
import PerformExam from "./PerformExam";
import PosturalExamSummary from "./PosturalExamSummary";
import PosturalExamWebcam from "./PosturalExamWebcam";
import {
  getPosturalExamDefinition,
  getPosturalExaminationData,
  getPosturalExamPhoto,
  uploadPosturalExamPhoto,
} from "./utils";
import PosturalExamSummary2 from "./PosturalExamSummary2";

const getNumPages = (type: string) => {
  const takePhotoPages: number = 1;
  const sumUpPages: number = 1;
  switch (type) {
    case EXAM_4PHOTOS_SLUGNAME:
      return takePhotoPages + 4 + sumUpPages;
    case EXAM_AAII_SLUGNAME:
      return takePhotoPages + 4 + sumUpPages;
    case EXAM_MAGNO_SLUGNAME:
      return takePhotoPages + 4 + sumUpPages;
    case EXAM_PODO_SLUGNAME:
      return takePhotoPages + 2 + sumUpPages;
    case EXAM_PODO_STIM_SLUGNAME:
      return takePhotoPages + 2 + sumUpPages;
    case EXAM_VISION_SLUGNAME:
      return takePhotoPages + 7 + sumUpPages;
    case EXAM_BALANCE_SLUGNAME:
      return takePhotoPages + 0 + sumUpPages;
    case EXAM_HEAD_SLUGNAME:
      return takePhotoPages + 3 + sumUpPages;
    case EXAM_FUKUDA_SLUGNAME:
      return takePhotoPages + 3 + sumUpPages;
    case EXAM_ADAMS_SLUGNAME:
      return takePhotoPages + 2 + sumUpPages;
    case EXAM_ROMBERG_SLUGNAME:
      return 2 + sumUpPages;
    case EXAM_TINETTI_SLUGNAME:
      return 2 + sumUpPages;
    case EXAM_RATE_SLUGNAME:
      return 4 + sumUpPages;
    case EXAM_ANAM_SLUGNAME:
      return 6 + sumUpPages;
    case EXAM_ELICOIDALE_SLUGNAME:
      return (
        takePhotoPages +
        4 +
        takePhotoPages +
        1 +
        takePhotoPages +
        3 +
        sumUpPages
      );
    default:
      return 0;
  }
};

const getPosturalExaminationDisplayTitle = (type: string) => {
  const prefix: string = "Esame posturale: ";
  switch (type) {
    case EXAM_4PHOTOS_SLUGNAME:
      return prefix + '"' + EXAM_4PHOTOS_FULLNAME + '"';
    case EXAM_AAII_SLUGNAME:
      return prefix + '"' + EXAM_AAII_FULLNAME + '"';
    case EXAM_MAGNO_SLUGNAME:
      return prefix + '"' + EXAM_MAGNO_FULLNAME + '"';
    case EXAM_PODO_SLUGNAME:
      return prefix + '"' + EXAM_PODO_FULLNAME + '"';
    case EXAM_PODO_STIM_SLUGNAME:
      return prefix + '"' + EXAM_PODO_STIM_FULLNAME + '"';
    case EXAM_VISION_SLUGNAME:
      return prefix + '"' + EXAM_VISION_FULLNAME + '"';
    case EXAM_BALANCE_SLUGNAME:
      return prefix + '"' + EXAM_BALANCE_FULLNAME + '"';
    case EXAM_HEAD_SLUGNAME:
      return prefix + '"' + EXAM_HEAD_FULLNAME + '"';
    case EXAM_FUKUDA_SLUGNAME:
      return prefix + '"' + EXAM_FUKUDA_FULLNAME + '"';
    case EXAM_ADAMS_SLUGNAME:
      return prefix + '"' + EXAM_ADAMS_FULLNAME + '"';
    case EXAM_ROMBERG_SLUGNAME:
      return prefix + '"' + EXAM_ROMBERG_FULLNAME + '"';
    case EXAM_RATE_SLUGNAME:
      return prefix + '"' + EXAM_RATE_FULLNAME + '"';
    case EXAM_ANAM_SLUGNAME:
      return prefix + '"' + EXAM_ANAM_FULLNAME + '"';
    case EXAM_TINETTI_SLUGNAME:
      return prefix + '"' + EXAM_TINETTI_FULLNAME + '"';
    case EXAM_ELICOIDALE_SLUGNAME:
      return prefix + '"' + EXAM_ELICOIDALE_FULLNAME + '"';
    default:
      return "";
  }
};

const numPhotosToTake = (type: string) => {
  switch (type) {
    case EXAM_4PHOTOS_SLUGNAME:
      return 4;
    case EXAM_BALANCE_SLUGNAME:
      return 3;
    case EXAM_AAII_SLUGNAME:
      return 1;
    case EXAM_MAGNO_SLUGNAME:
      return 2;
    case EXAM_PODO_SLUGNAME:
      return 1;
    case EXAM_PODO_STIM_SLUGNAME:
      return 1;
    case EXAM_VISION_SLUGNAME:
      return 1;
    case EXAM_HEAD_SLUGNAME:
      return 2;
    case EXAM_FUKUDA_SLUGNAME:
      return 2;
    case EXAM_ADAMS_SLUGNAME:
      return 2;
    case EXAM_ROMBERG_SLUGNAME:
      return 2;
    case EXAM_ELICOIDALE_SLUGNAME:
      return 8;
      case EXAM_RATE_SLUGNAME:
      return 0;

    default:
      return 0;
  }
  return 0;
};

const getIndexImageByExamType = (page: number, type: string) => {
  switch (type) {
    case EXAM_4PHOTOS_SLUGNAME:
      return page - 1;
    case EXAM_AAII_SLUGNAME:
      return 0;
    case EXAM_MAGNO_SLUGNAME:
      if (page < 3) return 0;
      else return 1;
    case EXAM_PODO_SLUGNAME:
      return 0;
    case EXAM_PODO_STIM_SLUGNAME:
      return 0;
    case EXAM_VISION_SLUGNAME:
      return 0;
    case EXAM_BALANCE_SLUGNAME:
      return 0;
    case EXAM_HEAD_SLUGNAME:
      if (page != 3) return 0;
      else return 1;
    case EXAM_FUKUDA_SLUGNAME:
      if (page == 1 || page == 2) return 0;
      else return 1;
    case EXAM_ADAMS_SLUGNAME:
      return page - 1;
    case EXAM_ROMBERG_SLUGNAME:
      return page - 1;
   
    case EXAM_ELICOIDALE_SLUGNAME:
      if (page === 1 || page === 2) {
        return 0;
      } else if (page === 3 || page === 4) {
        return 1;
      } else if (page === 5) {
        return 2;
      } else if (page === 6) {
        return 3;
      } else if (page === 7) {
        return 4;
      } else if (page === 8) {
        return 5;
      } else if (page === 9) {
        return 6;
      } else if (page === 10) {
        return 7;
      } else {
        return 7; // Se la pagina non corrisponde a nessuna delle condizioni precedenti, restituisce l'indice predefinito (0)
      }

    default:
      return 0;
  }
  return 0;
};

export interface TakenPhoto {
  uploaded: boolean;
  url: string;
  blob?: Blob;
}

export const photoNotTakenYet: TakenPhoto = {
  uploaded: false,
  url: "",
};

function getB64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.readAsDataURL(file);
    fr.onload = () => {
      if (typeof fr.result === "string") resolve(fr.result);
      else resolve("");
    };
    fr.onerror = () => reject(fr.error);
  });
}

function skipPhotosInThisExam(examType: string) {
  return (
    examType == EXAM_FUKUDA_SLUGNAME ||
    examType == EXAM_ROMBERG_SLUGNAME ||
    examType == EXAM_ANAM_SLUGNAME ||
    examType == EXAM_RATE_SLUGNAME ||
    examType == EXAM_TINETTI_SLUGNAME ||
    examType == EXAM_ELICOIDALE_SLUGNAME
  );
}

function skip2PhotosInThisExam(examType: string) {
  return (
    examType == EXAM_ROMBERG_SLUGNAME ||
    examType == EXAM_ANAM_SLUGNAME ||
    examType == EXAM_RATE_SLUGNAME ||
    examType == EXAM_TINETTI_SLUGNAME
  );
}

function GorderExam(examType: string) {
  return examType == EXAM_PODO_STIM_SLUGNAME;
}

//LEOB try2
function seePhotosInTheExamEli(examType: string) {
  return examType == EXAM_ELICOIDALE_SLUGNAME;
}

//LEOB Try1
function seePhotosInTheExam(examType: string) {
  return (
    examType == EXAM_4PHOTOS_SLUGNAME ||
    examType == EXAM_AAII_SLUGNAME ||
    examType == EXAM_BALANCE_SLUGNAME ||
    examType == EXAM_MAGNO_SLUGNAME ||
    examType == EXAM_PODO_SLUGNAME ||
    examType == EXAM_PODO_STIM_SLUGNAME ||
    examType == EXAM_VISION_SLUGNAME ||
    examType == EXAM_HEAD_SLUGNAME ||
    examType == EXAM_FUKUDA_SLUGNAME ||
    examType == EXAM_ADAMS_SLUGNAME
  );
}

function convertBase64ToBlob(base64Image: string) {
  // Split into two parts
  const parts = base64Image.split(";base64,");

  // Hold the content type
  const imageType = parts[0].split(":")[1];

  // Decode Base64 string
  const decodedData = window.atob(parts[1]);

  // Create UNIT8ARRAY of size same as row data length
  const uInt8Array = new Uint8Array(decodedData.length);

  // Insert all character code into uInt8Array
  for (let i = 0; i < decodedData.length; ++i) {
    uInt8Array[i] = decodedData.charCodeAt(i);
  }

  // Return BLOB image after conversion
  return new Blob([uInt8Array], { type: imageType });
}

const PosturalExamWrapper = () => {
  const urlSearchParam = new URLSearchParams(location.search);
  const justCreated: boolean = urlSearchParam.get("just_created") == "1"; //exam just created

  const { id } = useParams();
  const { examId } = useParams();
  const [patient, setPatient] = useState(emptyPatient);
  const [posturalExamination, setPosturalExamination] = useState(
    invalidPosturalExamination
  );
  const [
    posturalExaminationTestsDefinition,
    setPosturalExaminationTestsDefinition,
  ] = useState<PosturalTestDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const signedInDoctor: SignedInDoctor =
    useContext(SignedInContext).signedInDoctor;

  const [page, setPage] = useState(0);

  const [skipPhotos, setSkipPhotos] = useState(false);
  const [skip2Photos, setSkip2Photos] = useState(false);

  const [examsPhotos, setExamsPhotos] = useState(false);

  const [examsPhotoEli, setExamsPhotoEli] = useState(false);
  const [orderExam, setOrderExam] = useState(false);
  const [pVisiblie, setPVisiblie] = useState(false);

  const [takePhoto, setTakePhoto] = useState<number>(-1);
  const [allPhotosTaken, setAllPhotosTaken] = useState<boolean>(false);
  const [images, setImages] = useState<TakenPhoto[]>([]);
  const [dImagesUrl, setDImagesUrl] = useState<string[]>([]);

  const [modalSizeLimitVisible, setModalSizeLimitVisible] = useState(false);

  const getWebcamPhoto = (index: number) => setTakePhoto(index);
  const loadPhoto = async (index: number, file: File | string, fileType?: string) => {
    if (file instanceof File) {
      // TODO LEO: vedi se e come migliorare settings
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 2,
        maxWidthOrHeight: 768,
        fileType: fileType? fileType : "png",
        alwaysKeepResolution: true,
      });
      const b64 = await getB64(compressedFile);
      const blob = convertBase64ToBlob(b64);
      insertTakenPhoto(b64, blob, index);
    } else {
      //file is string url
      console.log(file);
      const response = await fetch(file);
      const blob = await response.blob();
      const filename = file.substring(file.lastIndexOf("/") + 1);
      const fileInstance = new File([blob], filename, { type: blob.type });
      const b64 = await getB64(fileInstance);
      insertTakenPhoto(b64, blob, index);
    }
    console.log("load photo %d", index);
  };

  const insertTakenPhoto = (
    imageB64: string,
    imageBlob: Blob,
    index: number
  ) => {
    setImages((oldImages: TakenPhoto[]) => {
      if (oldImages.length > index)
        oldImages[index] = { url: imageB64, uploaded: false, blob: imageBlob };
      else {
        while (oldImages.length < index) oldImages.push(photoNotTakenYet);

        oldImages.push({ url: imageB64, uploaded: false, blob: imageBlob }); //oldImages.length == takePhoto
      }
      return [...oldImages];
    });
  };

  const insertDrawnImageUrl = (imageB64: string, index: number) => {
    console.log("Inserting dimage " + index + " with len " + imageB64.length);
    setDImagesUrl((oldDImagesUrl: string[]) => {
      if (oldDImagesUrl.length > index) oldDImagesUrl[index] = imageB64;
      else {
        while (oldDImagesUrl.length < index) oldDImagesUrl.push("");
        oldDImagesUrl.push(imageB64); //oldImages.length == takePhoto
      }
      console.log(index);
      return [...oldDImagesUrl];
    });
  };

  const setFetchedPhoto = (blobUrl: string | undefined, index: number) => {
    if (blobUrl != undefined && blobUrl.length > 0)
      setImages((oldImages) => {
        while (oldImages.length < index) oldImages.push(photoNotTakenYet);
        if (oldImages.length == index)
          oldImages.push({ url: blobUrl, uploaded: true });
        else oldImages[index] = { url: blobUrl, uploaded: true };
        return [...oldImages];
      });
  };

  useEffect(() => {
    let counter = images.filter(
      (img) => img.url != photoNotTakenYet.url
    ).length;
    const counterPhotosToTake = numPhotosToTake(posturalExamination.type);
    setAllPhotosTaken(
      counterPhotosToTake > 0 && counter == counterPhotosToTake
    ); //all photos taken flag

    for (let i = 0; i < images.length; i++) {
      if (images[i].blob != undefined && !images[i].uploaded)
        uploadPosturalExamPhoto(
          images[i].blob,
          signedInDoctor,
          patient.id || -1,
          posturalExamination.examId,
          i
        ).then((newImgUrl) => {
          if (
            newImgUrl.indexOf(
              posturalExamination.patientId + "_" + posturalExamination.examId
            ) >= 0
          ) {
            getPosturalExamPhoto(
              signedInDoctor,
              posturalExamination.patientId,
              posturalExamination.examId,
              i
            ).then((blobUrl) => setFetchedPhoto(blobUrl, i));
          } else if (newImgUrl.indexOf("Storage size limit reached") >= 0)
            setModalSizeLimitVisible(true);
        });
    }
  }, [images]);

  useEffect(() => {
    getPatient(signedInDoctor, parseInt(id || "-1"))
      .then((fetchedPatient: Patient) => {
        setPatient({
          ...fetchedPatient,
        });
      })
      .catch((err) => console.error(err));

    getPosturalExaminationData(
      signedInDoctor,
      parseInt(id || "-1"),
      parseInt(examId || "-1"),
      true
    )
      .then((posturalExamination: PosturalExamination) => {
        setSkipPhotos(skipPhotosInThisExam(posturalExamination.type));
        setSkip2Photos(skip2PhotosInThisExam(posturalExamination.type));
        setExamsPhotos(seePhotosInTheExam(posturalExamination.type));
        setExamsPhotoEli(seePhotosInTheExamEli(posturalExamination.type));
        setOrderExam(GorderExam(posturalExamination.type));
        setPosturalExamination({
          ...posturalExamination,
        });

        getPosturalExamDefinition(
          signedInDoctor,
          posturalExamination.type
        ).then((testDefinitions) => {
          if (testDefinitions != undefined) {
            setPosturalExaminationTestsDefinition(testDefinitions);
            if (posturalExamination.testSelections) {
              let upd: boolean = false;
              //set passed value from definitions
              for (let selection of posturalExamination.testSelections) {
                const testDefinition: PosturalTestDefinition | null =
                  testDefinitions.filter((el) => el.id == selection.testId)
                    .length > 0
                    ? testDefinitions.filter(
                        (el) => el.id == selection.testId
                      )[0]
                    : null;
                if (testDefinition) {
                  const optionPassed =
                    testDefinition.posturalTestOptions.filter(
                      (opt) => opt.examPassedOption
                    );
                  selection.passedValue =
                    optionPassed.length > 0
                      ? optionPassed[0].posturalOptionId
                      : -2;
                  selection.prefixNote =
                    //"Test" +
                    //(testDefinition ? testDefinition?.id + "" : "") +
                    "[" + testDefinition?.posturalTestName + "]";
                  upd = true;
                }
              }
              if (upd)
                setPosturalExamination({
                  ...posturalExamination,
                });
            }
          }
        });

        for (let i = 0; i < numPhotosToTake(posturalExamination.type); i++) {
          getPosturalExamPhoto(
            signedInDoctor,
            posturalExamination.patientId,
            posturalExamination.examId,
            i
          ).then((blobUrl) => setFetchedPhoto(blobUrl, i));
        }
      })
      .catch((err) => console.error(err));
  }, [signedInDoctor]);

  useEffect(() => {
    if (
      patient.id != undefined &&
      patient.id > 0 &&
      posturalExamination.examId > 0 &&
      posturalExamination.patientId == patient.id
    )
      setLoading(false);
  }, [patient, posturalExamination]);

  const [width, setWidth] = useState(0);
  const handleResize = () => setWidth(window.innerWidth);

  useEffect(() => {
    //mount
    window.addEventListener("resize", handleResize);
    setWidth(window.innerWidth);
    //unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      {!loading && examsPhotos && (
        <>
          {width > 640 && (
            <div className="centered" style={{ marginBottom: "8px" }}>
              <h3 className="section-title">
                {loading && <LoadingOutlined rev={undefined} />}
                {getPosturalExaminationDisplayTitle(posturalExamination.type)}
              </h3>
              <h3>
                {patient.name} {patient.surname}
              </h3>
            </div>
          )}
          {width <= 640 && (
            <div className="centered" style={{ marginBottom: "8px" }}>
              <h3 className="section-title">
                {loading && <LoadingOutlined rev={undefined} />}
                {getPosturalExaminationDisplayTitle(posturalExamination.type)}
              </h3>
            </div>
          )}
          <Row>
            {width > 640 && (
              <Col span={12}>
                {page == 0 && examsPhotos && (
                  <PosturalExamWebcam
                    takePhoto={takePhoto}
                    posturalExamType={posturalExamination.type}
                    setTakePhoto={setTakePhoto}
                    insertPhoto={insertTakenPhoto}
                  />
                )}
                {page > 0 &&
                  page < getNumPages(posturalExamination.type) - 1 &&
                  examsPhotos && (
                    <ExamImage
                      index={getIndexImageByExamType(
                        page,
                        posturalExamination.type
                      )}
                      image={
                        images[
                          getIndexImageByExamType(
                            page,
                            posturalExamination.type
                          )
                        ]
                      }
                      examinationType={posturalExamination.type}
                      insertDrawnImage={insertDrawnImageUrl}
                    />
                  )}
                {page > 0 &&
                  page == getNumPages(posturalExamination.type) - 1 &&
                  images.length > 1 && (
                    <Carousel
                      style={{
                        maxHeight: "30rem",
                        maxWidth: "100%",
                        borderRadius: "25px",
                      }}
                      dotPosition={"left"}
                    >
                      {dImagesUrl.map((dimgUrl, index) => (
                        <div
                          style={{
                            maxHeight: "30rem",
                            maxWidth: "100%",
                            borderRadius: "25px",
                          }}
                          className="centered"
                          key={index}
                        >
                          <Image
                            style={{
                              maxHeight: "30rem",
                              maxWidth: "100%",
                              borderRadius: "25px",
                            }}
                            src={dimgUrl}
                          />
                        </div>
                      ))}
                      {
                        // TODO LEO problema con carousel quando foto di diverse risoluzione
                        //Carousel in ant.d https://4x.ant.design/components/carousel/#header
                      }
                    </Carousel>
                  )}
                {page > 0 &&
                  page == getNumPages(posturalExamination.type) - 1 &&
                  (dImagesUrl.length == 1 || images.length == 1) && (
                    <div className="centered">
                      <Image src={dImagesUrl[0] || images[0].url} />
                    </div>
                  )}
              </Col>
            )}
            {width <= 640 && (
              <>
                {page == 0 && examsPhotos && (
                  <PosturalExamWebcam
                    takePhoto={takePhoto}
                    posturalExamType={posturalExamination.type}
                    setTakePhoto={setTakePhoto}
                    insertPhoto={insertTakenPhoto}
                  />
                )}
              </>
            )}
            {width <= 640 && !pVisiblie && page > 0 && (
              <Button
                style={{ minWidth: "80%" }}
                onClick={() => setPVisiblie(true)}
              >
                Visualizza Immagine
              </Button>
            )}
            {width <= 640 && pVisiblie && (
              <>
                <Button
                  style={{ minWidth: "80%" }}
                  onClick={() => setPVisiblie(false)}
                >
                  Chiudi Immagine
                </Button>
                <Col span={20} className="daBos">
                  {page > 0 &&
                    page < getNumPages(posturalExamination.type) - 1 &&
                    examsPhotos && (
                      <ExamImage
                        index={getIndexImageByExamType(
                          page,
                          posturalExamination.type
                        )}
                        image={
                          images[
                            getIndexImageByExamType(
                              page,
                              posturalExamination.type
                            )
                          ]
                        }
                        examinationType={posturalExamination.type}
                        insertDrawnImage={insertDrawnImageUrl}
                      />
                    )}
                  {page > 0 &&
                    page == getNumPages(posturalExamination.type) - 1 &&
                    images.length > 1 && (
                      <Carousel
                        style={{
                          borderRadius: "25px",
                        }}
                        dotPosition={"left"}
                      >
                        {dImagesUrl.map((dimgUrl, index) => (
                          <div className="centered" key={index}>
                            <Image
                              style={{
                                borderRadius: "25px",
                              }}
                              src={dimgUrl}
                            />
                          </div>
                        ))}
                        {
                          // TODO LEO problema con carousel quando foto di diverse risoluzione
                          //Carousel in ant.d https://4x.ant.design/components/carousel/#header
                        }
                      </Carousel>
                    )}
                  {page > 0 &&
                    page == getNumPages(posturalExamination.type) - 1 &&
                    (dImagesUrl.length == 1 || images.length == 1) && (
                      <div className="centered">
                        <Image src={dImagesUrl[0] || images[0].url} />
                      </div>
                    )}
                </Col>
              </>
            )}
            {width > 640 && (
              <Col span={10}>
                {page < getNumPages(posturalExamination.type) - 1 && (
                  <PerformExam
                    loadPhoto={loadPhoto}
                    page={page}
                    images={images}
                    getWebcamPhoto={getWebcamPhoto}
                    posturalExamination={posturalExamination}
                    setPosturalExamination={setPosturalExamination}
                    testDefinitions={posturalExaminationTestsDefinition}
                    justCreated={justCreated}
                  />
                )}
                {page == getNumPages(posturalExamination.type) - 1 && (
                  <PosturalExamSummary
                    patient={patient}
                    posturalExamination={posturalExamination}
                    setPosturalExamination={setPosturalExamination}
                    setPatient={setPatient}
                  />
                )}
              </Col>
            )}
            {width <= 640 && (
              <Col span={20}>
                {page < getNumPages(posturalExamination.type) - 1 && (
                  <div className="upperok">
                    <PerformExam
                      loadPhoto={loadPhoto}
                      page={page}
                      images={images}
                      getWebcamPhoto={getWebcamPhoto}
                      posturalExamination={posturalExamination}
                      setPosturalExamination={setPosturalExamination}
                      testDefinitions={posturalExaminationTestsDefinition}
                      justCreated={justCreated}
                    />
                  </div>
                )}
                {page == getNumPages(posturalExamination.type) - 1 && (
                  <PosturalExamSummary
                    patient={patient}
                    posturalExamination={posturalExamination}
                    setPosturalExamination={setPosturalExamination}
                    setPatient={setPatient}
                  />
                )}
              </Col>
            )}
          </Row>
          <Row>
            <Col xs={16} sm={17} md={17} lg={21}></Col>
            <Col xs={8} sm={7} md={7} lg={3}>
              <span
                style={{
                  borderRadius: "25px",
                  marginRight: "5px",
                  marginBlock: "2px",
                  position: "fixed",
                  bottom: "0px",
                  marginBottom: "48px",
                  width: "100%",
                }}
              >
                {(allPhotosTaken || skipPhotos) && (
                  <>
                    {width > 640 && (
                      //Desktop
                      <>
                        {" "}
                        <div style={{ zoom: "1.3" }}>
                          <Button
                            style={{ zIndex: "999999999999999999" }}
                            type="default"
                            className="exam-page-btn"
                            disabled={page == 0}
                            onClick={() =>
                              setPage((oldPage) =>
                                oldPage == 0 ? oldPage : oldPage - 1
                              )
                            }
                          >
                            {"<"}
                          </Button>

                          <Button
                            style={{
                              zIndex: "999999999999999999",
                            }}
                            type="default"
                            className="exam-page-btn"
                            disabled={
                              !skipPhotos &&
                              (page ==
                                getNumPages(posturalExamination.type) - 1 ||
                                !allPhotosTaken)
                            }
                            onClick={() =>
                              setPage((oldPage) =>
                                oldPage ==
                                getNumPages(posturalExamination.type) - 1
                                  ? oldPage
                                  : oldPage + 1
                              )
                            }
                          >
                            {">"}
                          </Button>
                        </div>
                      </>
                    )}
                    {width <= 640 && (
                      //Mobile
                      <>
                        {" "}
                        <div className="nextExam-left">
                          <Button
                            style={{ zIndex: "999999999999999999" }}
                            type="default"
                            className="exam-page-btn-mobile-left"
                            disabled={page == 0}
                            onClick={() =>
                              setPage((oldPage) =>
                                oldPage == 0 ? oldPage : oldPage - 1
                              )
                            }
                          >
                            {"<"}
                          </Button>
                        </div>
                        <div className="nextExam">
                          <Button
                            style={{ zIndex: "999999999999999999" }}
                            type="default"
                            className="exam-page-btn-mobile"
                            disabled={
                              !skipPhotos &&
                              (page ==
                                getNumPages(posturalExamination.type) - 1 ||
                                !allPhotosTaken)
                            }
                            onClick={() =>
                              setPage((oldPage) =>
                                oldPage ==
                                getNumPages(posturalExamination.type) - 1
                                  ? oldPage
                                  : oldPage + 1
                              )
                            }
                          >
                            {">"}
                          </Button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </span>
            </Col>
          </Row>

          <Modal
            title="Mi dispiace :-("
            centered
            open={modalSizeLimitVisible}
            footer={false}
            onCancel={() => setModalSizeLimitVisible(false)}
            width={480}
          >
            <div className="centered">
              <h2>
                Purtroppo hai raggiunto il massimo dello spazio nello storage.
                Se vuoi effettuare altri esami, contatta l'assistenza per un
                upgrade.
              </h2>
            </div>
          </Modal>
        </>
      )}
      {!loading && examsPhotoEli && (
        <>
          {width > 640 && (
            <div className="centered" style={{ marginBottom: "8px" }}>
              <h3 className="section-title">
                {loading && <LoadingOutlined rev={undefined} />}
                {getPosturalExaminationDisplayTitle(posturalExamination.type)}
              </h3>
              <h3>
                {patient.name} {patient.surname}
              </h3>
            </div>
          )}
          {width <= 640 && (
            <div className="centered" style={{ marginBottom: "8px" }}>
              <h3 className="section-title" style={{ fontSize: "15px" }}>
                {loading && <LoadingOutlined rev={undefined} />}
                {getPosturalExaminationDisplayTitle(posturalExamination.type)}
              </h3>
            </div>
          )}
          <Row>
            {width > 640 && (
              //pc mode
              <Col span={12}>
                {(page === 0 || page === 5 || page === 7) && examsPhotoEli && (
                  <PosturalExamWebcam
                    takePhoto={takePhoto}
                    posturalExamType={posturalExamination.type}
                    setTakePhoto={setTakePhoto}
                    insertPhoto={insertTakenPhoto}
                  />
                )}
                {page > 0 &&
                  page !== 5 &&
                  page !== 7 &&
                  page < getNumPages(posturalExamination.type) - 1 &&
                  examsPhotoEli && (
                    <ExamImage
                      index={getIndexImageByExamType(
                        page,
                        posturalExamination.type
                      )}
                      image={
                        images[
                          getIndexImageByExamType(
                            page,
                            posturalExamination.type
                          )
                        ]
                      }
                      examinationType={posturalExamination.type}
                      insertDrawnImage={insertDrawnImageUrl}
                    />
                  )}
                {page > 0 && (page == 5 || page == 7) && examsPhotoEli && (
                  <>
                    <div className="magika">
                      <ExamImage
                        index={getIndexImageByExamType(
                          page,
                          posturalExamination.type
                        )}
                        image={
                          images[
                            getIndexImageByExamType(
                              page,
                              posturalExamination.type
                            )
                          ]
                        }
                        examinationType={posturalExamination.type}
                        insertDrawnImage={insertDrawnImageUrl}
                      />
                    </div>
                  </>
                )}
                {page > 0 &&
                  page !== 5 &&
                  page !== 7 &&
                  page == getNumPages(posturalExamination.type) - 1 &&
                  images.length > 1 && (
                    <Carousel
                      style={{
                        maxHeight: "30rem",
                        maxWidth: "100%",
                        borderRadius: "25px",
                      }}
                      dotPosition={"left"}
                    >
                      {dImagesUrl.map((dimgUrl, index) => (
                        <div
                          style={{
                            maxHeight: "30rem",
                            maxWidth: "100%",
                            borderRadius: "25px",
                          }}
                          className="centered"
                          key={index}
                        >
                          <Image
                            style={{
                              maxHeight: "30rem",
                              maxWidth: "100%",
                              borderRadius: "25px",
                            }}
                            src={dimgUrl}
                          />
                        </div>
                      ))}
                      {
                        // TODO LEO problema con carousel quando foto di diverse risoluzione
                        //Carousel in ant.d https://4x.ant.design/components/carousel/#header
                      }
                    </Carousel>
                  )}
              </Col>
            )}

            {width <= 640 && (
              <Col span={24}>
                {(page == 0 || page == 5 || page == 7) && examsPhotoEli && (
                  <PosturalExamWebcam
                    takePhoto={takePhoto}
                    posturalExamType={posturalExamination.type}
                    setTakePhoto={setTakePhoto}
                    insertPhoto={insertTakenPhoto}
                  />
                )}

                {page > 0 &&
                  page !== 5 &&
                  page !== 7 &&
                  page == getNumPages(posturalExamination.type) - 1 &&
                  (dImagesUrl.length == 1 || images.length == 1) && (
                    <div className="centered">
                      <Image src={dImagesUrl[0] || images[0].url} />
                    </div>
                  )}
              </Col>
            )}
            {width <= 640 &&
              !pVisiblie &&
              page > 0 &&
              page !== 5 &&
              page !== 7 && (
                <Button
                  style={{ minWidth: "80%" }}
                  onClick={() => setPVisiblie(true)}
                >
                  Visualizza Immagine
                </Button>
              )}
            {width <= 640 && pVisiblie && (
              <>
                <Button
                  style={{ minWidth: "80%" }}
                  onClick={() => setPVisiblie(false)}
                >
                  Chiudi Immagine
                </Button>
                <Col span={20} className="daBos">
                  {page > 0 &&
                    page !== 5 &&
                    page !== 7 &&
                    page < getNumPages(posturalExamination.type) - 1 &&
                    examsPhotoEli && (
                      <>
                        <ExamImage
                          index={getIndexImageByExamType(
                            page,
                            posturalExamination.type
                          )}
                          image={
                            images[
                              getIndexImageByExamType(
                                page,
                                posturalExamination.type
                              )
                            ]
                          }
                          examinationType={posturalExamination.type}
                          insertDrawnImage={insertDrawnImageUrl}
                        />
                      </>
                    )}
                  {page > 0 &&
                    page !== 5 &&
                    page !== 7 &&
                    page == getNumPages(posturalExamination.type) - 1 &&
                    images.length > 1 &&
                    pVisiblie && (
                      <Carousel
                        style={{
                          borderRadius: "25px",
                        }}
                        dotPosition={"left"}
                      >
                        {dImagesUrl.map((dimgUrl, index) => (
                          <div className="centered" key={index}>
                            <Image
                              style={{
                                borderRadius: "25px",
                              }}
                              src={dimgUrl}
                            />
                          </div>
                        ))}
                        {
                          // TODO LEO problema con carousel quando foto di diverse risoluzione
                          //Carousel in ant.d https://4x.ant.design/components/carousel/#header
                        }
                      </Carousel>
                    )}
                  {page > 0 &&
                    page == getNumPages(posturalExamination.type) - 1 &&
                    (dImagesUrl.length == 1 || images.length == 1) && (
                      <div className="centered">
                        <Image src={dImagesUrl[0] || images[0].url} />
                      </div>
                    )}
                </Col>
              </>
            )}
            {width > 640 && (
              <Col span={10}>
                {page < getNumPages(posturalExamination.type) - 1 && (
                  <PerformExam
                    loadPhoto={loadPhoto}
                    page={page}
                    images={images}
                    getWebcamPhoto={getWebcamPhoto}
                    posturalExamination={posturalExamination}
                    setPosturalExamination={setPosturalExamination}
                    testDefinitions={posturalExaminationTestsDefinition}
                    justCreated={justCreated}
                  />
                )}
                {page == getNumPages(posturalExamination.type) - 1 && (
                  <PosturalExamSummary
                    patient={patient}
                    posturalExamination={posturalExamination}
                    setPosturalExamination={setPosturalExamination}
                    setPatient={setPatient}
                  />
                )}
              </Col>
            )}
            {width <= 640 && (
              <Col span={20}>
                {page < getNumPages(posturalExamination.type) - 1 && (
                  <div className="upperok">
                    <PerformExam
                      loadPhoto={loadPhoto}
                      page={page}
                      images={images}
                      getWebcamPhoto={getWebcamPhoto}
                      posturalExamination={posturalExamination}
                      setPosturalExamination={setPosturalExamination}
                      testDefinitions={posturalExaminationTestsDefinition}
                      justCreated={justCreated}
                    />
                  </div>
                )}
                {page == getNumPages(posturalExamination.type) - 1 && (
                  <PosturalExamSummary
                    patient={patient}
                    posturalExamination={posturalExamination}
                    setPosturalExamination={setPosturalExamination}
                    setPatient={setPatient}
                  />
                )}
              </Col>
            )}
          </Row>
          <Row>
            <Col xs={16} sm={17} md={17} lg={21}></Col>
            <Col xs={8} sm={7} md={7} lg={3}>
              <span
                style={{
                  borderRadius: "25px",
                  marginRight: "5px",
                  marginBlock: "2px",
                  position: "fixed",
                  bottom: "0px",
                  marginBottom: "48px",
                  width: "100%",
                }}
              >
                {(allPhotosTaken || skipPhotos) && (
                  <>
                    {width > 640 && (
                      //Desktop
                      <>
                        {" "}
                        <div style={{ zoom: "1.3" }}>
                          <Button
                            style={{ zIndex: "999999999999999999" }}
                            type="default"
                            className="exam-page-btn"
                            disabled={page == 0}
                            onClick={() =>
                              setPage((oldPage) =>
                                oldPage == 0 ? oldPage : oldPage - 1
                              )
                            }
                          >
                            {"<"}
                          </Button>

                          <Button
                            style={{
                              zIndex: "999999999999999999",
                            }}
                            type="default"
                            className="exam-page-btn"
                            disabled={
                              !skipPhotos &&
                              (page ==
                                getNumPages(posturalExamination.type) - 1 ||
                                !allPhotosTaken)
                            }
                            onClick={() =>
                              setPage((oldPage) =>
                                oldPage ==
                                getNumPages(posturalExamination.type) - 1
                                  ? oldPage
                                  : oldPage + 1
                              )
                            }
                          >
                            {">"}
                          </Button>
                        </div>
                      </>
                    )}
                    {width <= 640 && (
                      //Mobile
                      <>
                        {" "}
                        <div className="nextExam-left">
                          <Button
                            style={{ zIndex: "999999999999999999" }}
                            type="default"
                            className="exam-page-btn-mobile-left"
                            disabled={page == 0}
                            onClick={() =>
                              setPage((oldPage) =>
                                oldPage == 0 ? oldPage : oldPage - 1
                              )
                            }
                          >
                            {"<"}
                          </Button>
                        </div>
                        <div className="nextExam">
                          <Button
                            style={{ zIndex: "999999999999999999" }}
                            type="default"
                            className="exam-page-btn-mobile"
                            disabled={
                              !skipPhotos &&
                              (page ==
                                getNumPages(posturalExamination.type) - 1 ||
                                !allPhotosTaken)
                            }
                            onClick={() =>
                              setPage((oldPage) =>
                                oldPage ==
                                getNumPages(posturalExamination.type) - 1
                                  ? oldPage
                                  : oldPage + 1
                              )
                            }
                          >
                            {">"}
                          </Button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </span>
            </Col>
          </Row>

          <Modal
            title="Mi dispiace :-("
            centered
            open={modalSizeLimitVisible}
            footer={false}
            onCancel={() => setModalSizeLimitVisible(false)}
            width={480}
          >
            <div className="centered">
              <h2>
                Purtroppo hai raggiunto il massimo dello spazio nello storage.
                Se vuoi effettuare altri esami, contatta l'assistenza per un
                upgrade.
              </h2>
            </div>
          </Modal>
        </>
      )}

      {!loading && skip2Photos && (
        <>
          <div className="centered" style={{ marginBottom: "8px" }}>
            <h3 className="section-title">
              {loading && <LoadingOutlined rev={undefined} />}
              {getPosturalExaminationDisplayTitle(posturalExamination.type)}
            </h3>
            <h3>
              {patient.name} {patient.surname}
            </h3>
          </div>
          <Row>
            <Col span={24}>
              {page == 0 && examsPhotos && <></>}
              {page > 0 &&
                page < getNumPages(posturalExamination.type) - 1 &&
                examsPhotos && <></>}
              {page > 0 &&
                page == getNumPages(posturalExamination.type) - 1 &&
                images.length > 1 && (
                  <Carousel dotPosition={"left"}>
                    {dImagesUrl.map((dimgUrl, index) => (
                      <div className="centered" key={index}>
                        <Image src={dimgUrl} />
                      </div>
                    ))}
                    {
                      // TODO LEO problema con carousel quando foto di diverse risoluzione
                      //Carousel in ant.d https://4x.ant.design/components/carousel/#header
                    }
                  </Carousel>
                )}
              {page > 0 &&
                page == getNumPages(posturalExamination.type) - 1 &&
                (dImagesUrl.length == 1 || images.length == 1) && (
                  <div className="centered"></div>
                )}
            </Col>
            <Col span={23}>
              {page < getNumPages(posturalExamination.type) - 1 && (
                <PerformExam
                  loadPhoto={loadPhoto}
                  page={page}
                  images={images}
                  getWebcamPhoto={getWebcamPhoto}
                  posturalExamination={posturalExamination}
                  setPosturalExamination={setPosturalExamination}
                  testDefinitions={posturalExaminationTestsDefinition}
                  justCreated={justCreated}
                />
              )}
              {page == getNumPages(posturalExamination.type) - 1 && (
                <PosturalExamSummary2
                  patient={patient}
                  posturalExamination={posturalExamination}
                  setPosturalExamination={setPosturalExamination}
                  setPatient={setPatient}
                />
              )}
            </Col>
          </Row>
          <Row>
            <Col xs={16} sm={17} md={17} lg={21}></Col>
            <Col xs={8} sm={7} md={7} lg={3}>
              <span
                className="theButton"
                style={{
                  marginRight: "5px",
                  marginBlock: "2px",
                  position: "fixed",
                  bottom: "0px",
                  marginBottom: "48px",
                  width: "100%",
                }}
              >
                {(allPhotosTaken || skipPhotos) && (
                  <>
                    {width > 640 && (
                      //Desktop
                      <>
                        {" "}
                        <div style={{ zoom: "1.3" }}>
                          <Button
                            style={{ zIndex: "999999999999999999" }}
                            type="default"
                            className="exam-page-btn"
                            disabled={page == 0}
                            onClick={() =>
                              setPage((oldPage) =>
                                oldPage == 0 ? oldPage : oldPage - 1
                              )
                            }
                          >
                            {"<"}
                          </Button>
                        </div>
                        <div style={{ zoom: "1.3" }}>
                          <Button
                            style={{ zIndex: "999999999999999999" }}
                            type="default"
                            className="exam-page-btn"
                            disabled={
                              !skipPhotos &&
                              (page ==
                                getNumPages(posturalExamination.type) - 1 ||
                                !allPhotosTaken)
                            }
                            onClick={() =>
                              setPage((oldPage) =>
                                oldPage ==
                                getNumPages(posturalExamination.type) - 1
                                  ? oldPage
                                  : oldPage + 1
                              )
                            }
                          >
                            {">"}
                          </Button>
                        </div>
                      </>
                    )}
                    {width <= 640 && (
                      //Mobile
                      <>
                        {" "}
                        <div className="nextExam-left">
                          <Button
                            style={{ zIndex: "999999999999999999" }}
                            type="default"
                            className="exam-page-btn-mobile-left"
                            disabled={page == 0}
                            onClick={() =>
                              setPage((oldPage) =>
                                oldPage == 0 ? oldPage : oldPage - 1
                              )
                            }
                          >
                            {"<"}
                          </Button>
                        </div>
                        <div className="nextExam">
                          <Button
                            style={{ zIndex: "999999999999999999" }}
                            type="default"
                            className="exam-page-btn-mobile"
                            disabled={
                              !skipPhotos &&
                              (page ==
                                getNumPages(posturalExamination.type) - 1 ||
                                !allPhotosTaken)
                            }
                            onClick={() =>
                              setPage((oldPage) =>
                                oldPage ==
                                getNumPages(posturalExamination.type) - 1
                                  ? oldPage
                                  : oldPage + 1
                              )
                            }
                          >
                            {">"}
                          </Button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </span>
            </Col>
          </Row>

          <Modal
            title="Mi dispiace :-("
            centered
            open={modalSizeLimitVisible}
            footer={false}
            onCancel={() => setModalSizeLimitVisible(false)}
            width={480}
          >
            <div className="centered">
              <h2>
                Purtroppo hai raggiunto il massimo dello spazio nello storage.
                Se vuoi effettuare altri esami, contatta l'assistenza per un
                upgrade.
              </h2>
            </div>
          </Modal>
        </>
      )}
    </>
  );
};

export default PosturalExamWrapper;
